import { useCallback, useState } from "react";

import type { DocDetail } from "./types.js";

import { saveBlob } from "../../lib/download.js";
import { fetchDocFileBytes, getDoc } from "./api.js";
import { buildDocPdf } from "./docPdf.js";

/**
 * One code path for the folder page's per-row "Download PDF" action
 * (`CategoryPage` → `ArticleRow` — a row carries only a `DocSummary` and so
 * passes a slug, and the hook fetches the detail). Tracks the
 * busy slug + last error so the row can show a spinner / message. Saving goes
 * through the shared `saveBlob` — a native Save-As dialog in the desktop app,
 * browser download on the web (X12).
 *
 * NB: the article READER (`ArticlePage`) does not use this — its one
 * "Download" button downloads the ORIGINAL file for uploaded docs and only
 * builds a PDF for manually-authored (body-only) docs (2026-06-27). The list
 * rows still always offer "Download PDF" as a uniform per-row export.
 */

export interface DocPdfDownload {
  /** Build + save the PDF for a doc. Pass a `DocDetail` to skip the refetch
   * (the reader has it), or a slug (list rows). */
  download: (target: string | DocDetail) => Promise<void>;
  /** Slug currently being generated, or `null` — drives per-row spinners. */
  busySlug: string | null;
  error: string | null;
  clearError: () => void;
}

export function useDocPdfDownload(): DocPdfDownload {
  const [busySlug, setBusySlug] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const download = useCallback(async (target: string | DocDetail): Promise<void> => {
    const slug = typeof target === "string" ? target : target.slug;
    setBusySlug(slug);
    setError(null);
    try {
      const detail = typeof target === "string" ? (await getDoc(target)).data : target;
      const { blob, filename } = await buildDocPdf(detail, {
        fetchFileBytes: fetchDocFileBytes,
      });
      await saveBlob(filename, blob);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not generate the PDF");
    } finally {
      setBusySlug(null);
    }
  }, []);

  return { download, busySlug, error, clearError: () => setError(null) };
}
